// lib
import { useState, useEffect, useCallback, useMemo, Children } from 'react';
// mui
import { Box, Card, ListItemText, Typography } from '@mui/material';
// components
import { LoadingScreen } from 'src/components/loading-screen';
// types
import { IDetailsTableKeys, IOverlayItem } from 'src/types';
import { useLocales } from 'src/locales';

export default function OverlayContentView({
  currentOverlay,
  isLoading,
}: {
  currentOverlay: IOverlayItem;
  isLoading: boolean;
}) {
  const { t } = useLocales('overlayPage');
  const [modOverlay, setModOverlay] = useState<IOverlayItem | undefined>(undefined);

  const productDetailsKeys: { [section: string]: IDetailsTableKeys[] } = useMemo(
    () => ({
      overlayInfo: [
        { key: 'title', label: 'title' },
        { key: 'startDate', label: 'startDate' },
        { key: 'endDate', label: 'endDate' },
        { key: 'createdAt', label: 'createdAt' },
        { key: 'updatedAt', label: 'updatedAt' },
      ],
      status: [
        {
          key: 'active',
          label: t('active'),
        },
        {
          key: 'isFallback',
          label: t('fallback'),
        },
      ],
    }),
    [t],
  );

  function formatDateToLocaleString(dateString: string | number | Date) {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  }

  const modifyProductHandler = useCallback((overlay: IOverlayItem) => {
    const currOverlay: IOverlayItem = {
      ...overlay,
      startDate: overlay.startDate ? formatDateToLocaleString(overlay.startDate) : undefined,
      endDate: overlay.endDate ? formatDateToLocaleString(overlay.endDate) : undefined,
      createdAt: overlay.createdAt ? formatDateToLocaleString(overlay.createdAt) : undefined,
      updatedAt: overlay.updatedAt ? formatDateToLocaleString(overlay.updatedAt) : undefined,
    };
    setModOverlay(currOverlay);
  }, []);

  useEffect(() => {
    if (currentOverlay) {
      modifyProductHandler(currentOverlay);
    }
  }, [currentOverlay, modifyProductHandler]);

  if (isLoading)
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <LoadingScreen />
      </Box>
    );

  return (
    <Card>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          margin: 2,
        }}
      >
        {modOverlay && (
          <>
            {Children.toArray(
              Object.entries(productDetailsKeys).map(
                ([sectionTitle, keys]) =>
                  sectionTitle !== 'content' && (
                    <Box
                      key={sectionTitle}
                      sx={{
                        width: '100%',
                        mb: 2,
                      }}
                    >
                      <Typography variant="h6" gutterBottom>
                        {t(sectionTitle)}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                        }}
                      >
                        {Children.toArray(
                          keys.map((item) => (
                            <Box
                              sx={{
                                padding: 1,
                                width: { xs: '100%', sm: item?.fullWidth ? '100%' : '50%' },
                              }}
                            >
                              <ListItemText
                                primary={t(item.label)}
                                secondary={(() => {
                                  if (item.key === 'active' || item.key === 'isFallback') {
                                    return modOverlay[item.key as keyof IOverlayItem]
                                      ? t('yes')
                                      : t('no');
                                  }
                                  if (
                                    typeof modOverlay[item.key as keyof IOverlayItem] === 'boolean'
                                  ) {
                                    return modOverlay[item.key as keyof IOverlayItem]
                                      ? t('yes')
                                      : t('no');
                                  }
                                  const value = modOverlay[item.key as keyof IOverlayItem];
                                  return value instanceof Date
                                    ? value.toISOString()
                                    : value || '----';
                                })()}
                                slotProps={{
                                  primary: {
                                    sx: {
                                      typography: 'body2',
                                      mb: 0.5,
                                    },
                                    color: 'text.secondary',
                                  },

                                  secondary: {
                                    sx: {
                                      typography: 'subtitle2',
                                    },
                                    color: 'text.primary',
                                    component: 'span',
                                  },
                                }}
                              />
                            </Box>
                          )),
                        )}
                      </Box>
                    </Box>
                  ),
              ),
            )}
            {modOverlay.content && (
              <Box
                sx={{
                  width: '100%',
                  mb: 2,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  {t('content')}
                </Typography>
                <Box
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: 2,
                    maxHeight: '300px',
                    overflowY: 'auto',
                  }}
                >
                  <Typography
                    component="div"
                    dangerouslySetInnerHTML={{
                      __html: modOverlay.content.replace(/<p><br><\/p>/g, '') || '',
                    }}
                  />
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Card>
  );
}
